import { Button, Icon, RangePicker, type DateRange } from '@forecastify/ui';
import { addWireDays, toWireDate, wireMonday } from '@forecastify/shared';
import { useI18n, useT } from '../../i18n';
import { useCalendarLabels } from '../date-field';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE DASHBOARDS' PERIOD CONTROL — the mockup's `RangePicker`, at last. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Both dashboards had two bare `<input type="date">` boxes stacked in a corner, which is the
 * platform's own control and not this product's — CLAUDE.md's five settled controls say so about
 * `<select>` for the same reason, and the mockup draws `RangePicker` here by name.
 *
 * ⚠️ **The eight presets are the mockup's own list** (`presetsFor`), and they are relative to
 * today rather than typed: *ez a hét*, *ebben a hónapban*, *ebben a negyedévben*, the next two of
 * each, and the previous two. A preset list that needed maintaining every quarter would be wrong
 * within one.
 *
 * ⚠️ **`twoUp`, because a range is chosen ACROSS a boundary.** One month at a time makes *27 July –
 * 20 September* a scroll-and-remember exercise; the component takes the prop precisely so a caller
 * that knows it has the width can ask for both.
 *
 * ⚠️⚠️ **THE ARROWS ARE THE POINT OF THE CONTROL, AND THEY WERE MISSING — 15 Sep.** Reading a
 * dashboard is *this week, then the one before it, then the one before that*, and every step of it
 * went through opening a calendar and clicking two dates. The picker stays for jumping somewhere;
 * the arrows are for walking.
 *
 * ⚠️⚠️ **A STEP WAS THE WINDOW'S OWN LENGTH AND IS NOW ONE COLUMN — round 5 item 6.** The old
 * rule was that back means the nine days before a nine-day window, with no gap and no overlap; it
 * is a defensible rule and it is the wrong one for a grid. With sixteen weeks on screen, one press
 * replaced every column at once and left nothing in common between the two views — the reader has
 * to find their place again rather than follow it. *"A stepper 1 hetet lépjen előre, nem a teljes
 * kijelölt időszakot, napiban pedig napit."*
 *
 * ⚠️ **So the caller says how far**, because only it knows whether a column is a week or a day.
 * The window's LENGTH is untouched: stepping slides it, it never resizes it.
 *
 * ⚠️⚠️ **AND *MA* BRINGS IT HOME — 3 Nov 2026, the product owner (`decisions.md` DASH-1).**
 * Somebody who has walked eight weeks back had to walk eight weeks forward, or open the picker and
 * lose the length they chose. The button keeps the length and moves the START: to today on a daily
 * grid, to this Monday on a weekly one, to the first of the month where a column is a month.
 */
export function RangeControl({
  value,
  onChange,
  defaultRange,
  stepDays = 7,
  anchor,
}: {
  readonly value: DateRange;
  readonly onChange: (range: DateRange) => void;
  readonly defaultRange: DateRange;
  /**
   * ⚠️ **How far one press moves the window — one COLUMN, in days.** 7 on a weekly grid, 1 on a
   * daily one. Defaulted so a caller with no columns to speak of keeps walking by the week.
   */
  readonly stepDays?: number;
  /**
   * Where *Ma* starts the window. Absent, it follows `stepDays`: a one-day step is a daily grid
   * (today), anything else a weekly one (this Monday). The leave chart's month and quarter zooms
   * pass `month`.
   */
  readonly anchor?: 'day' | 'week' | 'month';
}) {
  const t = useT();
  const { locale } = useI18n();
  const calendar = useCalendarLabels();
  const today = toWireDate(new Date());

  const monthRange = (offset: number): DateRange => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const last = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
    return { from: toWireDate(first), to: toWireDate(last) };
  };

  const quarterRange = (offset: number): DateRange => {
    const now = new Date();
    const quarter = Math.floor(now.getMonth() / 3) + offset;
    const first = new Date(now.getFullYear(), quarter * 3, 1);
    const last = new Date(first.getFullYear(), first.getMonth() + 3, 0);
    return { from: toWireDate(first), to: toWireDate(last) };
  };

  const monday = wireMonday(today) ?? today;

  /**
   * ⚠️ **Inclusive at both ends**, so a Monday-to-Sunday window steps by seven and lands on the
   * next Monday. `(to - from) + 1` is the day count; `addWireDays` does the arithmetic, because a
   * calendar date and an instant do not share a converter (`hard-won.md`).
   */
  const step = (direction: -1 | 1): void => {
    /* ⚠️ BOTH ends by the same amount, which is what keeps the window the length somebody
       chose. The old code measured the window to step by it; nothing needs measuring now. */
    const shift = direction * stepDays;
    const nextFrom = addWireDays(value.from, shift);
    const nextTo = addWireDays(value.to, shift);
    if (nextFrom === null || nextTo === null) return;
    onChange({ from: nextFrom, to: nextTo });
  };

  const jumpToday = (): void => {
    const from = todayStart(today, anchor ?? (stepDays === 1 ? 'day' : 'week'));
    const days = windowDays(value) ?? windowDays(defaultRange) ?? 1;
    const to = addWireDays(from, days - 1);
    if (to === null) return;
    onChange({ from, to });
  };

  return (
    <div className="flex items-center gap-1" data-control-row>
      <Button
        variant="outline"
        size="icon-sm"
        aria-label={t('home.chart.previous')}
        onClick={() => {
          step(-1);
        }}
      >
        <Icon name="chevron-left" size={14} />
      </Button>
      <RangePicker
        value={value}
        /**
         * ⚠️⚠️ **THE BUTTON SAYS WHICH PERIOD, NOT THE WORD *PERIOD* — 15 Sep.** The one control
         * that tells somebody what a dashboard is showing read *Időszak* whatever it was set to.
         *
         * ⚠️ **Through `Intl`, with the locale** — a hand-built `2026.09.12 – 09.19` would be a
         * third opinion about how this product writes a date, and Hungarian and English do not
         * write a range the same way.
         */
        format={(range) => formatRange(range.from, range.to, locale)}
        defaultRange={defaultRange}
        onChange={onChange}
        today={today}
        twoUp
        labels={{
          ...calendar,
          apply: t('range.apply'),
          reset: t('range.reset'),
          trigger: t('range.trigger'),
        }}
        presets={[
          { label: t('range.preset.default'), ...defaultRange },
          { label: t('range.preset.thisWeek'), from: monday, to: addWireDays(monday, 6) ?? monday },
          { label: t('range.preset.thisMonth'), ...monthRange(0) },
          { label: t('range.preset.thisQuarter'), ...quarterRange(0) },
          { label: t('range.preset.nextMonth'), ...monthRange(1) },
          { label: t('range.preset.nextQuarter'), ...quarterRange(1) },
          { label: t('range.preset.lastMonth'), ...monthRange(-1) },
          { label: t('range.preset.lastQuarter'), ...quarterRange(-1) },
        ]}
      />
      {/* ⚠️ Between the picker and the forward arrow — the product owner's placement. The kit
          Button's `title` is the kit tooltip, never the browser's. ⚠️ 4 Nov 2026 · no height of
          its own: `sm` is the row's 32px, and the arrows are brought to it by `[data-control-row]`
          — an `h-7` here left it the one short control. */}
      <Button
        variant="outline"
        size="sm"
        className="px-2.5 text-[12px]"
        title={t('range.todayHint')}
        aria-label={t('range.todayHint')}
        data-range-today
        onClick={jumpToday}
      >
        {t('range.today')}
      </Button>
      <Button
        variant="outline"
        size="icon-sm"
        aria-label={t('home.chart.next')}
        onClick={() => {
          step(1);
        }}
      >
        <Icon name="chevron-right" size={14} />
      </Button>
    </div>
  );
}

/** Inclusive day count of a wire range — `null` for anything unparseable or inverted. */
export function windowDays(range: { readonly from: string; readonly to: string }): number | null {
  const from = Date.parse(`${range.from}T00:00:00Z`);
  const to = Date.parse(`${range.to}T00:00:00Z`);
  if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) return null;
  return Math.round((to - from) / 86_400_000) + 1;
}

/** Where *Ma* starts a window: today, this Monday, or the first of this month. */
function todayStart(today: string, anchor: 'day' | 'week' | 'month'): string {
  if (anchor === 'day') return today;
  if (anchor === 'month') return `${today.slice(0, 8)}01`;
  return wireMonday(today) ?? today;
}

/**
 * A wire range, written the way this locale writes one.
 *
 * ⚠️ **`Intl.DateTimeFormat.formatRange` where the engine has it**: it collapses the shared parts by
 * itself — *szept. 12–19.* rather than *szept. 12. – szept. 19.* — and it knows that Hungarian and
 * English disagree about how. The fallback joins the two, which is never wrong, only longer.
 */
function formatRange(from: string, to: string, locale: string): string {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    /* ⚠️ UTC both in the parse and in the format — a wire date is a calendar date, and rendering it
       in the viewer's zone is how the 1st becomes the 31st west of Greenwich (`hard-won.md`). */
    timeZone: 'UTC',
  };
  try {
    const formatter = new Intl.DateTimeFormat(locale, options);
    const start = new Date(`${from}T00:00:00Z`);
    const end = new Date(`${to}T00:00:00Z`);
    return formatter.formatRange(start, end);
  } catch {
    return `${from} – ${to}`;
  }
}
